/**
 * UndoToast Component (AC-574)
 *
 * Provides a quick undo action for recent saves with a countdown timer.
 * Appears when lastSave is set in versionStore and auto-dismisses after 10 seconds.
 *
 * Parent Component: Global/App level (rendered in layout or root component)
 *
 * Features:
 * - Shows message: "{Entity type} saved. Undo?"
 * - 10-second countdown timer (updates every second)
 * - Auto-dismisses after timeout by calling clearLastSave()
 * - "Undo" button calls undoLastSave(), shows loading, then success/error
 * - "Dismiss" (X) button calls clearLastSave()
 * - After successful undo, shows "Undone!" briefly then dismisses
 * - Position prop controls placement (default: 'bottom-right')
 * - Uses role="alert" for accessibility
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';

const TIMEOUT_SECONDS = 10;
const SUCCESS_DISMISS_DELAY = 1500;

interface UndoToastProps {
  position?: 'top-right' | 'bottom-right' | 'bottom-center';
}

const UndoToast: React.FC<UndoToastProps> = ({ position = 'bottom-right' }) => {
  const { lastSave, undoLastSave, clearLastSave } = useVersionStore();
  const [countdown, setCountdown] = useState(TIMEOUT_SECONDS);
  const [undoing, setUndoing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to track scheduled clear timeout
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state when lastSave changes
  useEffect(() => {
    if (!lastSave) {
      setCountdown(TIMEOUT_SECONDS);
      setUndoing(false);
      setSuccess(false);
      setError(null);
      return;
    }

    // Reset countdown when new lastSave appears
    setCountdown(TIMEOUT_SECONDS);
    setSuccess(false);
    setError(null);
    setUndoing(false);
  }, [lastSave]);

  // Countdown and auto-dismiss logic
  useEffect(() => {
    if (!lastSave || success) {
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearLastSave();
          return TIMEOUT_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [lastSave, clearLastSave, success]);

  // Clear toast after success message is shown
  useEffect(() => {
    if (!success) {
      return;
    }

    // Schedule clear after showing success message briefly
    clearTimeoutRef.current = setTimeout(() => {
      clearLastSave();
    }, SUCCESS_DISMISS_DELAY);

    return () => {
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
      }
    };
  }, [success, clearLastSave]);

  // Handle undo - optimistically schedule clear, cancel on error
  const handleUndo = useCallback(async () => {
    setUndoing(true);
    setError(null);

    // Optimistically schedule the clear timeout. We'll cancel it if there's an error.
    // This ensures the timeout is registered before any test timer advancement.
    clearTimeoutRef.current = setTimeout(() => {
      clearLastSave();
    }, SUCCESS_DISMISS_DELAY);

    try {
      const result = await undoLastSave();
      if (result) {
        setSuccess(true);
        // Timeout already scheduled, will fire and call clearLastSave
      } else {
        // Cancel the optimistic timeout on failure
        if (clearTimeoutRef.current) {
          clearTimeout(clearTimeoutRef.current);
          clearTimeoutRef.current = null;
        }
        setError('Failed to undo');
      }
    } catch (err) {
      // Cancel the optimistic timeout on error
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
        clearTimeoutRef.current = null;
      }
      setError(err instanceof Error ? err.message : 'Failed to undo');
    } finally {
      setUndoing(false);
    }
  }, [undoLastSave, clearLastSave]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    clearLastSave();
  }, [clearLastSave]);

  if (!lastSave) {
    return null;
  }

  // Position classes based on prop
  const positionClasses: Record<typeof position, string> = {
    'top-right': 'top-4 right-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  };

  // Capitalize entity type for display
  const entityLabel =
    lastSave.entity_type.charAt(0).toUpperCase() + lastSave.entity_type.slice(1);

  return (
    <div
      role="alert"
      className={`fixed ${positionClasses[position]} z-50 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[280px] max-w-[360px]`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          {success ? (
            <p className="text-sm font-medium text-green-600">Undone!</p>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-warm-white">
                {entityLabel} saved. Undo?
              </p>
              {error && (
                <p className="text-sm text-red-600 mt-1">{error}</p>
              )}
              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={handleUndo}
                  disabled={undoing}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {undoing ? 'Undoing...' : 'Undo'}
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">({countdown})</span>
              </div>
            </>
          )}
        </div>
        {!success && (
          <button
            onClick={handleDismiss}
            className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default UndoToast;
